import {convertImgToBase64URLNoCors} from "../../misc/utils";

// Cuts an image into 4 equal pieces (2x2) on a canvas and returns them as base64 data urls.
// Used for the "multiImage" AI results (one image built out of 4 variants, see isMultiImage
// in aiApi): clicking "edit" on one of the pieces sends that single piece back to the
// backend as the user photo (see createCommonPostData in aiApi).
//
// The pieces are returned row by row, in the same order they are rendered on screen:
// 0 - top left, 1 - top right, 2 - bottom left, 3 - bottom right.

export const AI_MULTI_IMAGE_PIECES = 4;
export const AI_MULTI_IMAGE_COLUMNS = 2;
export const AI_MULTI_IMAGE_ROWS = 2;

const PIECE_MIME_TYPE = "image/png";

// where a piece sits in the 2x2 grid - used to animate it out of its own corner
// (see the edit mode of LookAIImageOverlayImageContent)
export const getPieceGridPosition = (pieceIndex: number): {row: number, column: number} => {
  return {
    row: Math.floor(pieceIndex / AI_MULTI_IMAGE_COLUMNS),
    column: pieceIndex % AI_MULTI_IMAGE_COLUMNS,
  };
};

const _loadImageElement = (src: string, useCrossOrigin: boolean): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (useCrossOrigin) {
      image.crossOrigin = "anonymous"; // required, otherwise the canvas gets tainted (s3 images)
    }
    image.onload = () => resolve(image);
    image.onerror = (e) => reject(e);
    image.src = src;
  });
};

// Draws every piece of an already loaded image. Returns null (instead of throwing) when the
// canvas is tainted - the caller then retries with a base64 copy of the same image.
const _cutLoadedImage = (image: HTMLImageElement): string[] | null => {
  const pieceWidth = Math.floor((image.naturalWidth || image.width) / AI_MULTI_IMAGE_COLUMNS);
  const pieceHeight = Math.floor((image.naturalHeight || image.height) / AI_MULTI_IMAGE_ROWS);
  if (!pieceWidth || !pieceHeight) {
    return null;
  }
  const pieces: string[] = [];
  for (let row = 0; row < AI_MULTI_IMAGE_ROWS; row++) {
    for (let column = 0; column < AI_MULTI_IMAGE_COLUMNS; column++) {
      const canvas = document.createElement("canvas");
      canvas.width = pieceWidth;
      canvas.height = pieceHeight;
      const context = canvas.getContext("2d");
      if (!context) {
        return null;
      }
      context.drawImage(
        image,
        column * pieceWidth, row * pieceHeight, pieceWidth, pieceHeight, // source rectangle
        0, 0, pieceWidth, pieceHeight, // destination rectangle
      );
      try {
        pieces.push(canvas.toDataURL(PIECE_MIME_TYPE));
      } catch (e) {
        return null; // tainted canvas
      }
    }
  }
  return pieces;
};

export const cutImageIntoFourPieces = async (imageUrl: string): Promise<string[]> => {
  if (!imageUrl) {
    return [];
  }
  const isLocalImage = imageUrl.indexOf("data:") === 0 || imageUrl.indexOf("blob:") === 0;

  if (!isLocalImage) {
    try {
      const corsImage = await _loadImageElement(imageUrl, true);
      const corsPieces = _cutLoadedImage(corsImage);
      if (corsPieces) {
        return corsPieces;
      }
    } catch (e) {
      // no CORS headers (or a cached non-CORS response) - fall through to the base64 copy below
    }
  }

  try {
    const localUrl = isLocalImage ? imageUrl : ((await convertImgToBase64URLNoCors(imageUrl)) as string);
    if (!localUrl) {
      return [];
    }
    const image = await _loadImageElement(localUrl, false);
    return _cutLoadedImage(image) || [];
  } catch (e) {
    console.log("cutImageIntoFourPieces error", e);
    return [];
  }
};

export const cutImagePiece = async (imageUrl: string, pieceIndex: number): Promise<string> => {
  const pieces = await cutImageIntoFourPieces(imageUrl);
  return pieces[pieceIndex] || "";
};
